import { Component, computed, inject, input, linkedSignal, output, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { form, FormField, maxLength, minLength, pattern, required } from '@angular/forms/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { StepperModule } from 'primeng/stepper';
import { IFunpayOnboardingInitialData, IFunpayOnboardingPrefill } from '../../../../../services/funpay.service';
import { DEFAULT_INDUSTRY_CODE, FUNPAY_INDUSTRY_CODES } from '../industry-codes';

/**Can not be longer than 22 */
export const SHOPPER_STATEMENT_MAX_LENGTH = 22;

export const SHOPPER_STATEMENT_PATTERN = /^[A-Za-z0-9 .,'-]+$/;

/** Adyems own E164 phone number regexes*/
const E164_PATTERN = /^\+[1-9]\d{6,14}$/;
const OPTIONAL_E164_PATTERN = /^(\+[1-9]\d{6,14})?$/;

/** Adyen rejects a registered address in these countries without a state or province. */
const STATE_REQUIRED_COUNTRIES = ['US', 'CA', 'AU', 'NZ'];

type FieldWithErrors = { errors: () => { kind: string }[] } | undefined;

@Component({
    selector: 'funpay-onboarding-form',
    templateUrl: './onboarding-form.html',
    imports: [StepperModule, ButtonModule, InputTextModule, SelectModule, FloatLabelModule, FormField, FormsModule, NgClass, TranslatePipe]
})
export class FunPayOnboardingFormComponent {
    prefill = input<IFunpayOnboardingPrefill | null>(null);
    submitting = input<boolean>(false);
    submitError = input<string | null>(null);

    submitted = output<IFunpayOnboardingInitialData>();

    activeStep = signal<number>(1);

    private translate = inject(TranslateService);

    readonly shopperStatementMaxLength = SHOPPER_STATEMENT_MAX_LENGTH;

    industryOptions = computed(() =>
        FUNPAY_INDUSTRY_CODES.map((code) => ({ value: code.value, label: this.translate.instant(code.labelKey) }))
    );

    onboardingData = linkedSignal<IFunpayOnboardingInitialData>(() => {
        const prefill = this.prefill();

        return {
            legalName: prefill?.legalName ?? '',
            industryCode: prefill?.industryCode || DEFAULT_INDUSTRY_CODE,
            shopperStatement: (prefill?.shopperStatement ?? '').slice(0, SHOPPER_STATEMENT_MAX_LENGTH),
            phoneNumberSignatory: prefill?.phoneNumberSignatory ?? '',
            phoneNumberVenue: prefill?.phoneNumberVenue ?? '',
            street: prefill?.street ?? '',
            city: prefill?.city ?? '',
            postalCode: prefill?.postalCode ?? '',
            stateOrProvince: prefill?.stateOrProvince ?? ''
        };
    });

    country = computed(() => this.prefill()?.country ?? '');

    stateRequired = computed(() => STATE_REQUIRED_COUNTRIES.includes(this.country()));

    onboardingForm = form(this.onboardingData, (data) => {
        required(data.legalName);
        maxLength(data.legalName, 150);

        required(data.industryCode);

        required(data.shopperStatement);
        minLength(data.shopperStatement, 3);
        maxLength(data.shopperStatement, SHOPPER_STATEMENT_MAX_LENGTH);
        pattern(data.shopperStatement, SHOPPER_STATEMENT_PATTERN);

        required(data.phoneNumberVenue);
        pattern(data.phoneNumberVenue, E164_PATTERN);

        pattern(data.phoneNumberSignatory, OPTIONAL_E164_PATTERN);

        // Adyen drops street, city and postalCode unless all three are present, so the
        required(data.street);
        maxLength(data.street, 200);
        required(data.city);
        maxLength(data.city, 100);
        required(data.postalCode);
        maxLength(data.postalCode, 20);
        required(data.stateOrProvince, { when: () => this.stateRequired() });
    });

    businessStepValid = computed(
        () =>
            !!this.onboardingForm.legalName().valid() &&
            !!this.onboardingForm.industryCode().valid() &&
            !!this.onboardingForm.phoneNumberSignatory().valid()
    );

    venueStepValid = computed(
        () =>
            !!this.onboardingForm.shopperStatement().valid() &&
            !!this.onboardingForm.phoneNumberVenue().valid() &&
            !!this.onboardingForm.street().valid() &&
            !!this.onboardingForm.city().valid() &&
            !!this.onboardingForm.postalCode().valid() &&
            !!this.onboardingForm.stateOrProvince().valid()
    );

    formValid = computed(() => this.businessStepValid() && this.venueStepValid());

    selectedIndustryLabel = computed(
        () => this.industryOptions().find((option) => option.value === this.onboardingData().industryCode)?.label ?? ''
    );

    shopperStatementLength = computed(() => this.onboardingData().shopperStatement.length);

    setIndustryCode(industryCode: string): void {
        this.onboardingData.update((data) => ({ ...data, industryCode }));
    }

    goToStep(step: number): void {
        this.activeStep.set(step);
    }

    /** Translation key describing why a field is invalid. */
    fieldError(field: FieldWithErrors): string {
        const errors = field?.errors?.() ?? [];

        if (errors.some((error) => error.kind === 'required')) {
            return 'FUNPAY_FIELD_REQUIRED';
        }
        if (errors.some((error) => error.kind === 'minLength')) {
            return 'FUNPAY_FIELD_TOO_SHORT';
        }
        if (errors.some((error) => error.kind === 'maxLength')) {
            return 'FUNPAY_FIELD_TOO_LONG';
        }
        if (errors.some((error) => error.kind === 'pattern')) {
            return 'FUNPAY_FIELD_INVALID_FORMAT';
        }

        return 'FUNPAY_FIELD_INVALID';
    }

    submit(): void {
        if (!this.formValid() || this.submitting()) {
            return;
        }

        const data = this.onboardingData();

        this.submitted.emit({
            ...data,
            legalName: data.legalName.trim(),
            shopperStatement: data.shopperStatement.trim(),
            phoneNumberSignatory: data.phoneNumberSignatory.trim(),
            phoneNumberVenue: data.phoneNumberVenue.trim(),
            street: data.street.trim(),
            city: data.city.trim(),
            postalCode: data.postalCode.trim(),
            stateOrProvince: data.stateOrProvince.trim()
        });
    }
}
